import { Container, type Texture } from 'pixi.js';
import type { SlotGameModel } from '../../model/slot/SlotGameModel';
import { ActionButton } from '../../widgets/ActionButton';
import { GambleUI, type GambleDecide, type GambleResult } from '../../widgets/slot/GambleUI';
import type { SceneComponent, SceneLayoutContext } from '../SceneComponent';

export interface GambleFlowCallbacks {
  /** Render an ad-hoc message in the scene's result label. */
  setResultText: (text: string) => void;
  /** Player chose to collect (or auto-collect on cap/loss with banked
   * winnings). The host scene runs the take-win + balance credit and resolves
   * when the animation finishes. The scene's CollectLogic clears the model's
   * pendingWinnings as part of its execute. */
  collect: () => Promise<void>;
  /** The gamble flow has fully resolved (lose, or post-collect). The scene
   * should restore the idle state — show spin button, re-enable bet selector,
   * etc. */
  onExit: () => void;
  /** Fires true as the double-or-nothing overlay opens and false once it
   *  resolves, so hosts can move their own overlays out of the way. */
  onGambleOpenChange?: (open: boolean) => void;
  /** Who decides the next gamble: the server when the win came from a
   *  server spin, otherwise null for a local draw. Asked as each gamble
   *  opens. */
  decider?: () => GambleDecide | null;
  /** The slot's reel floor, for the gamble board (looked up when it opens). */
  floor?: () => Texture | null;
}

/**
 * The "gamble for ×2 or collect" overlay shown between a winning spin and the
 * take-win animation. Owns the visible GAMBLE / COLLECT buttons, the
 * colour-pick gamble UI, and the gamble streak state machine.
 *
 * <p>Pending winnings + streak counter live on the {@link SlotGameModel};
 * this component just decides which buttons to show and which state to
 * transition into. The actual coin-flying take-win animation is the scene's
 * responsibility — this component asks for it via the {@code collect}
 * callback.
 */
export class GambleFlowComponent extends Container implements SceneComponent {
  private readonly gambleUI: GambleUI;
  private readonly gambleButton: ActionButton;
  private readonly collectButton: ActionButton;

  constructor(
    private readonly model: SlotGameModel,
    private readonly maxGambleStreak: number,
    private readonly callbacks: GambleFlowCallbacks,
  ) {
    super();

    this.gambleUI = new GambleUI();
    if (callbacks.floor) this.gambleUI.setFloor(callbacks.floor);
    this.addChild(this.gambleUI);

    this.gambleButton = new ActionButton('GAMBLE', 0xff8a3d, () => {
      void this.handleGamble();
    });
    this.gambleButton.visible = false;
    this.addChild(this.gambleButton);

    this.collectButton = new ActionButton('COLLECT', 0xfff59d, () => {
      void this.handleCollect();
    });
    this.collectButton.visible = false;
    this.addChild(this.collectButton);
  }

  layout(ctx: SceneLayoutContext): void {
    this.gambleButton.x = ctx.centerX - 100;
    this.gambleButton.y = ctx.spinButtonY;
    this.collectButton.x = ctx.centerX + 100;
    this.collectButton.y = ctx.spinButtonY;
    this.gambleUI.layout(ctx.width, ctx.height);
  }

  destroy(): void {
    this.gambleUI.destroyGamble();
    super.destroy({ children: true });
  }

  /** Begin the gamble offer for a freshly-landed win. The host scene is
   * responsible for resetting the streak (via {@code model.resetGambleStreak()})
   * before calling this — that lets the streak persist across consecutive
   * gambles within the same win.
   *
   * <p>2026-06-04 producer brief: the floating GAMBLE / COLLECT
   * buttons are GONE. This method now just stages pendingWinnings on
   * the model so the host can route SPIN (auto-collect via
   * {@link collectPending}) and the inline HUD GAMBLE button
   * (via {@link openGambleOverlay}). The dual-button .visible lines
   * are kept disabled inline so a future bring-back is one delete. */
  offerWinnings(winnings: number): void {
    this.model.setPendingWinnings(winnings);
    // this.gambleButton.visible = true;  // intentionally disabled
    // this.collectButton.visible = true; // intentionally disabled
    this.callbacks.setResultText(this.offerLabel(winnings));
  }

  /** Open the red/black card-pick overlay directly — wired to the
   *  HUD's inline GAMBLE medallion. Same effect as the old modal
   *  GAMBLE button onTap, exposed publicly so the scene can fire it
   *  without going through the now-defunct dual-button stage.
   *
   *  <p>Returns the promise so the caller can await the overlay's
   *  resolution and re-show the inline HUD button if {@code
   *  pendingWinnings} survived (= player won the gamble and a re-
   *  offer is staged). Lose / cap paths drop pendingWinnings to 0
   *  and call {@code onExit} → {@code exitToIdle} so the inline
   *  button stays hidden then. */
  async openGambleOverlay(): Promise<void> {
    await this.handleGamble();
  }

  /** Collect pending winnings + run the take-win animation. Wired to
   *  the SPIN auto-collect path: if the player taps SPIN while a win
   *  is pending, the scene calls this first, awaits the take-win,
   *  then proceeds with the next spin. */
  collectPending(): Promise<void> {
    return this.handleCollect();
  }

  /** Hide the gamble + collect buttons. Used by the scene to clean up if the
   * flow needs to be aborted (e.g. layout edge case). */
  hideButtons(): void {
    this.gambleButton.visible = false;
    this.collectButton.visible = false;
  }

  // -------- private flow --------

  private async handleGamble(): Promise<void> {
    if (this.model.gambleStreak >= this.maxGambleStreak) {
      // Defensive: cap should already prevent the button from being tappable,
      // but force-collect if we somehow get here.
      void this.handleCollect();
      return;
    }
    this.gambleButton.visible = false;
    this.collectButton.visible = false;

    this.callbacks.onGambleOpenChange?.(true);
    let result: GambleResult | null;
    try {
      result = await this.gambleUI.play(this.model.pendingWinnings, this.callbacks.decider?.() ?? null);
    } finally {
      this.callbacks.onGambleOpenChange?.(false);
    }
    if (result === null) {
      // No answer from the server: nothing changed, so the same win is
      // offered again (SPIN still collects it).
      this.offerWinnings(this.model.pendingWinnings);
      return;
    }
    const streakAfter = this.model.incrementGambleStreak();
    this.model.setPendingWinnings(result.winningsAfter);
    this.model.events.emit({ kind: 'gamble-resolved', result, streakAfter });

    if (result.outcome === 'lose') {
      // Lose — winnings vanish (already set to 0 in winningsAfter). Show the
      // outcome and exit the flow; scene returns to idle.
      this.callbacks.setResultText(`${result.picked} → ${result.actualColor} · LOST  ✕`);
      this.callbacks.onExit();
    } else if (streakAfter >= this.maxGambleStreak) {
      // Cap reached on a winning gamble — auto-collect after a beat so the
      // player sees the "won the gamble" moment before the take-win starts.
      this.callbacks.setResultText(
        `${this.model.pendingWinnings.toLocaleString()} — gamble cap reached, collecting`,
      );
      setTimeout(() => void this.handleCollect(), 400);
    } else {
      // Won and can gamble again — re-offer.
      this.offerWinnings(this.model.pendingWinnings);
    }
  }

  private async handleCollect(): Promise<void> {
    this.gambleButton.visible = false;
    this.collectButton.visible = false;
    if (this.model.pendingWinnings > 0) {
      this.callbacks.setResultText(`+${this.model.pendingWinnings.toLocaleString()} collected`);
      await this.callbacks.collect();
    } else {
      // Came in from a losing gamble. Drain the model's pendingWinnings
      // explicitly so nothing leaks across spins.
      this.model.setPendingWinnings(0);
    }
    this.callbacks.onExit();
  }

  private offerLabel(winnings: number): string {
    const remaining = this.maxGambleStreak - this.model.gambleStreak;
    return `${winnings.toLocaleString()} — GAMBLE for ×2 or COLLECT (${remaining} gambles left)`;
  }
}
